/**
 * ApiKeysPage — Main page for managing external API keys.
 *
 * Sections:
 *   1. Overview stat cards (derived from key list data)
 *   2. API Key cards (active + revoked) with Edit, Rotate, Revoke
 *   3. Recent API Activity (audit logs with pagination)
 *   4. Security Events (auth failures, IP blocks)
 *
 * Dual-Key Auth: key_id (public, mk_id_...) + api_secret (private, mk_secret_...)
 *
 * Route: /dashboard/api-keys
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Row, Col, Button, Typography, Tag, Table, Statistic,
  Skeleton, Empty, Modal, message, Tooltip, Badge, Tabs,
} from 'antd';
import {
  KeyOutlined, PlusOutlined, CopyOutlined, SyncOutlined,
  DeleteOutlined, ClockCircleOutlined, ApiOutlined,
  ThunderboltOutlined, HistoryOutlined, ReloadOutlined,
  EditOutlined, GlobalOutlined, ExclamationCircleOutlined,
  SafetyOutlined, WarningOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import ApiKeyService from '../../../../../services/apiKeyService';
import GenerateKeyModal from './GenerateKeyModal';
import KeyRevealModal from './KeyRevealModal';
import EditKeyModal from './EditKeyModal';
import ApiPlanGate from './ApiPlanGate';
import { useOnboarding } from '../../../../../context/onboardingContext';
import './ApiKeys.css';

const { Title, Text } = Typography;

export default function ApiKeysPage() {
  // ── Plan gate check — enterprise only ──────────────────────
  const { onboardingStatus, loadingOnboarding } = useOnboarding();
  const hasApiAccess = (onboardingStatus?.plan_name || '').toLowerCase() === 'enterprise';

  // ── State ───────────────────────────────────────────────────
  const [keys, setKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(true);

  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditLoading, setAuditLoading] = useState(false);
  const AUDIT_PAGE_SIZE = 10;

  // Security events
  const [securityEvents, setSecurityEvents] = useState([]);
  const [securityTotal, setSecurityTotal] = useState(0);
  const [securityPage, setSecurityPage] = useState(1);
  const [securityLoading, setSecurityLoading] = useState(false);
  const SECURITY_PAGE_SIZE = 10;

  const [generateModalOpen, setGenerateModalOpen] = useState(false);

  // Dual-key reveal state (3 credentials)
  const [revealedKeyId, setRevealedKeyId] = useState(null);
  const [revealedSecret, setRevealedSecret] = useState(null);
  const [revealedApiKey, setRevealedApiKey] = useState(null);
  const [revealedWebhookSecret, setRevealedWebhookSecret] = useState(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);



  // ── Derived stats from key list ────────────────────────────
  const stats = useMemo(() => {
    const active = keys.filter((k) => k.is_active !== false);
    const revoked = keys.filter((k) => k.is_active === false);
    const totalRequests = keys.reduce((sum, k) => sum + (Number(k.total_requests) || 0), 0);
    const withWebhook = keys.filter((k) => k.webhook_url).length;
    return { active: active.length, revoked: revoked.length, totalKeys: keys.length, totalRequests, withWebhook };
  }, [keys]);

  // ── Data fetching ──────────────────────────────────────────
  const fetchKeys = useCallback(async () => {
    try {
      setKeysLoading(true);
      const res = await ApiKeyService.listKeys();
      if (res.success) setKeys(Array.isArray(res.entity) ? res.entity : []);
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
    } finally {
      setKeysLoading(false);
    }
  }, []);

  const fetchAuditLogs = useCallback(async (page = 1) => {
    try {
      setAuditLoading(true);
      const res = await ApiKeyService.getAuditLogs({ page, limit: AUDIT_PAGE_SIZE });
      if (res.success) {
        const entity = res.entity || {};
        setAuditLogs(Array.isArray(entity.logs) ? entity.logs : Array.isArray(entity) ? entity : []);
        setAuditTotal(entity.pagination?.total || entity.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasApiAccess) { fetchKeys(); fetchAuditLogs(1); }
  }, [hasApiAccess, fetchKeys, fetchAuditLogs]);

  // ── Handlers ───────────────────────────────────────────────
  const fetchSecurityEvents = useCallback(async (page = 1) => {
    try {
      setSecurityLoading(true);
      const res = await ApiKeyService.getSecurityEvents({ page, limit: SECURITY_PAGE_SIZE });
      if (res.success) {
        const entity = res.entity || {};
        setSecurityEvents(Array.isArray(entity.logs) ? entity.logs : Array.isArray(entity) ? entity : []);
        setSecurityTotal(entity.pagination?.total || entity.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch security events:', err);
    } finally {
      setSecurityLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasApiAccess) { fetchSecurityEvents(1); }
  }, [hasApiAccess, fetchSecurityEvents]);

  const handleGenerateSuccess = (result) => {
    setGenerateModalOpen(false);
    const entity = result.entity || {};

    // Dual-key: keyId + apiSecret + apiKey (legacy)
    const kid = entity.keyId || entity.key_id || null;
    const sec = entity.apiSecret || entity.api_secret || null;
    const legKey = entity.apiKey || entity.api_key || null;
    const whs = entity.webhookSecret || entity.webhook_secret || null;

    setRevealedKeyId(typeof kid === 'string' ? kid : null);
    setRevealedSecret(typeof sec === 'string' ? sec : null);
    setRevealedApiKey(typeof legKey === 'string' ? legKey : null);
    setRevealedWebhookSecret(typeof whs === 'string' ? whs : null);
    fetchKeys();
  };

  const handleEdit = (key) => { setEditingKey(key); setEditModalOpen(true); };
  const handleEditSuccess = () => { setEditModalOpen(false); setEditingKey(null); fetchKeys(); };

  const handleRevoke = (key) => {
    Modal.confirm({
      title: 'Revoke API Key?',
      content: (
        <div>
          <p>This will permanently disable <strong>{key.key_name || key.keyName}</strong>.</p>
          <p style={{ color: '#ff4d4f' }}>Any integrations using this key will immediately stop working. This action cannot be undone.</p>
        </div>
      ),
      okText: 'Revoke Key',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const id = key._id || key.id;
          const res = await ApiKeyService.revokeKey(id);
          if (res.success) { message.success('API key revoked'); fetchKeys(); }
          else message.error(typeof res.error === 'string' ? res.error : 'Failed to revoke key');
        } catch (err) {
          const msg = err?.response?.data?.message || err?.response?.data?.error;
          message.error(typeof msg === 'string' ? msg : 'Failed to revoke key');
        }
      },
    });
  };

  const handleRotate = (key) => {
    Modal.confirm({
      title: 'Rotate API Secret?',
      content: (
        <div>
          <p>This will generate a new <strong>api_secret</strong> for <strong>{key.key_name || key.keyName}</strong> and invalidate the current one.</p>
          <p>Your <strong>key_id</strong> will remain unchanged. You will be shown the new secret <strong>once</strong>.</p>
        </div>
      ),
      okText: 'Rotate Secret',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const id = key._id || key.id;
          const res = await ApiKeyService.rotateKey(id);
          if (res.success) {
            const entity = res.entity || {};
            // Rotation returns new credentials: keyId + apiSecret + apiKey (legacy)
            const kid = entity.keyId || entity.key_id || key.key_id || key.keyId || null;
            const sec = entity.apiSecret || entity.api_secret || null;
            const legKey = entity.apiKey || entity.api_key || null;
            setRevealedKeyId(typeof kid === 'string' ? kid : null);
            setRevealedSecret(typeof sec === 'string' ? sec : null);
            setRevealedApiKey(typeof legKey === 'string' ? legKey : null);
            setRevealedWebhookSecret(null);
            message.success('Credentials rotated successfully');
            fetchKeys();
          } else {
            message.error(typeof res.error === 'string' ? res.error : 'Failed to rotate secret');
          }
        } catch (err) {
          const msg = err?.response?.data?.message || err?.response?.data?.error;
          message.error(typeof msg === 'string' ? msg : 'Failed to rotate secret');
        }
      },
    });
  };

  const handleCopyKeyId = async (keyId) => {
    try { await navigator.clipboard.writeText(keyId); message.success('Key ID copied'); }
    catch { message.info('Could not copy to clipboard'); }
  };

  // ── Audit log table columns ────────────────────────────────
  const auditColumns = [
    {
      title: 'Time', dataIndex: 'created_at', key: 'time', width: 140,
      render: (val) => (
        <Tooltip title={moment(val).format('YYYY-MM-DD HH:mm:ss')}>
          <span style={{ fontSize: 13 }}>{moment(val).fromNow()}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Endpoint', dataIndex: 'endpoint', key: 'endpoint',
      render: (val, record) => (
        <Text code style={{ fontSize: 12 }}>{record.method || 'POST'} {val}</Text>
      ),
    },
    {
      title: 'Status', dataIndex: 'status_code', key: 'status', width: 80,
      render: (val) => (
        <Tag className="audit-status-tag" color={val >= 200 && val < 300 ? 'success' : val >= 400 && val < 500 ? 'warning' : 'error'}>
          {val}
        </Tag>
      ),
    },
    {
      title: 'Key', dataIndex: 'key_name', key: 'key_name', width: 160,
      render: (val) => <Text ellipsis style={{ maxWidth: 140 }}>{val || '—'}</Text>,
    },
    {
      title: 'IP', dataIndex: 'ip_address', key: 'ip', width: 130,
      render: (val) => <Text type="secondary" style={{ fontSize: 12 }}>{val || '—'}</Text>,
    },
  ];

  // ── Plan Gate ──────────────────────────────────────────────
  if (loadingOnboarding) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (!hasApiAccess) return <ApiPlanGate />;

  // ── Main Render ────────────────────────────────────────────
  return (
    <div className="api-keys-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h2 className="api-keys-page-title">API Keys</h2>
          <p className="api-keys-page-subtitle">Manage your API keys and monitor usage</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => setGenerateModalOpen(true)}>
          Generate New Key
        </Button>
      </div>

      {/* Overview Stat Strip */}
      <div className="api-stats-strip">
        {[
          { title: 'Active Keys', value: stats.active, icon: <KeyOutlined />, color: '#10b981', bg: '#ecfdf5' },
          { title: 'Total Keys', value: stats.totalKeys, icon: <ApiOutlined />, color: '#3b82f6', bg: '#eff6ff' },
          { title: 'Total Requests', value: stats.totalRequests, icon: <ThunderboltOutlined />, color: '#f59e0b', bg: '#fffbeb' },
          { title: 'With Webhooks', value: stats.withWebhook, icon: <GlobalOutlined />, color: '#8b5cf6', bg: '#f5f3ff' },
        ].map((stat) => (
          <div key={stat.title} className="api-stat-item">
            <div className="api-stat-icon" style={{ background: stat.bg, color: stat.color }}>
              {stat.icon}
            </div>
            <div>
              <div className="api-stat-value" style={{ color: stat.color }}>{keysLoading ? '—' : (stat.value || 0).toLocaleString()}</div>
              <div className="api-stat-label">{stat.title}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs: Your API Keys + Recent API Activity */}
      <Tabs
        defaultActiveKey="keys"
        size="large"
        className="api-keys-tabs"
        items={[
          {
            key: 'keys',
            label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><KeyOutlined /> Your API Keys {keys.length > 0 && <Badge count={keys.length} style={{ backgroundColor: '#1890ff' }} />}</span>,
            children: (
              <>
                {keysLoading ? (
                  <Skeleton active paragraph={{ rows: 4 }} />
      ) : keys.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <Empty description={<span>No API keys yet.<br />Generate your first key to start integrating.</span>}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setGenerateModalOpen(true)}>Generate First Key</Button>
          </Empty>
        </Card>
      ) : (
        keys.map((key) => {
          const isActive = key.is_active !== false && key.isActive !== false;
          const isRevoked = key.revokedAt || key.revoked_at;
          const keyName = key.key_name || key.keyName || 'Unnamed Key';
          const keyId = key.key_id || key.keyId || key.key_prefix || key.keyPrefix || 'mk_id_••••••••';
          const keyPrefix = key.keyPrefix || key.key_prefix || null;
          const authMode = key.authMode || key.auth_mode || 'dual';
          const scopes = Array.isArray(key.scopes) ? key.scopes : [];
          const rateLimit = key.rate_limit_per_minute || key.rateLimitPerMinute;
          const totalReq = key.total_requests || key.totalRequests || 0;
          const webhookUrl = key.webhook_url || key.webhookUrl;
          const allowedIps = Array.isArray(key.allowed_ips || key.allowedIps) ? (key.allowed_ips || key.allowedIps) : [];
          const expiresAt = key.expires_at || key.expiresAt;
          const isExpiringSoon = expiresAt && moment(expiresAt).diff(moment(), 'days') <= 7 && moment(expiresAt).isAfter(moment());
          const isExpired = expiresAt && moment(expiresAt).isBefore(moment());
          const failedAttempts = key.failedAttempts || key.failed_attempts || 0;
          const disabledReason = key.disabledReason || key.disabled_reason || null;
          const isDisabled = !!disabledReason;
          const isLegacy = authMode !== 'dual';

          return (
            <Card key={key._id || key.id} className={`api-key-card ${isRevoked ? 'revoked' : ''} ${isDisabled ? 'disabled-key' : ''}`}>
              <div className="key-header">
                <div className="key-name-wrap">
                  <div className="key-name">
                    <Badge status={isRevoked ? 'error' : isDisabled ? 'warning' : 'success'} />
                    {keyName}
                  </div>
                  <div className="key-state-row">
                    {isRevoked && <Tag color="error">Revoked</Tag>}
                    {isDisabled && !isRevoked && <Tag color="warning">Disabled</Tag>}
                    {isExpired && isActive && !isDisabled && <Tag color="warning">Expired</Tag>}
                    {isExpiringSoon && isActive && !isExpired && !isDisabled && (
                      <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                        Expires {moment(expiresAt).fromNow()}
                      </Tag>
                    )}
                    {failedAttempts > 0 && !isRevoked && (
                      <Tooltip title={`${failedAttempts} failed authentication attempt${failedAttempts > 1 ? 's' : ''}`}>
                        <Tag color="orange" icon={<WarningOutlined />}>
                          ⚠️ {failedAttempts} failed
                        </Tag>
                      </Tooltip>
                    )}
                  </div>
                </div>
                <div className="key-actions">
                  {!isRevoked && !isDisabled ? (
                    <>
                      <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(key)}>Edit</Button>
                      <Button size="small" icon={<SyncOutlined />} onClick={() => handleRotate(key)}>Rotate</Button>
                      <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleRevoke(key)}>Revoke</Button>
                    </>
                  ) : isDisabled && !isRevoked ? (
                    <>
                      <Button size="small" type="primary" icon={<SyncOutlined />} onClick={() => handleRotate(key)}>
                        Rotate to re-enable
                      </Button>
                      <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleRevoke(key)}>Revoke</Button>
                    </>
                  ) : null}
                </div>
              </div>

              <div className="key-card-grid">
                <div className="key-card-left">
                  {isDisabled && !isRevoked && (
                    <div className="disabled-reason-banner">
                      <WarningOutlined style={{ color: '#fa8c16' }} />
                      <Text type="warning" style={{ fontSize: 12 }}>
                        {disabledReason === 'exceeded_max_failed_attempts'
                          ? 'Auto-disabled: exceeded 10 failed auth attempts. Rotate to re-enable.'
                          : disabledReason || 'Key has been disabled'}
                      </Text>
                    </div>
                  )}

                  <div className="key-preview-row">
                    <span className="key-preview">
                      <Tag color="blue" style={{ margin: 0, fontFamily: 'monospace', fontSize: 12 }}>key_id</Tag>
                      {keyId}
                    </span>
                    <Tooltip title="Copy key ID">
                      <Button size="small" type="text" icon={<CopyOutlined />} onClick={() => handleCopyKeyId(keyId)} />
                    </Tooltip>
                  </div>

                  <div className="key-badges-row">
                    <Tag color={authMode === 'dual' ? 'blue' : 'default'} style={{ fontSize: 11, margin: 0 }}>
                      {authMode === 'dual' ? '🔐 Dual-Key' : '🔑 Legacy'}
                    </Tag>
                    {rateLimit && (
                      <Tag style={{ fontSize: 11, margin: 0 }}>
                        <ThunderboltOutlined /> {rateLimit}/min
                      </Tag>
                    )}
                    {allowedIps.length > 0 ? (
                      <Tag color="green" style={{ fontSize: 11, margin: 0 }}>
                        <SafetyOutlined /> {allowedIps.length} IP{allowedIps.length > 1 ? 's' : ''} whitelisted
                      </Tag>
                    ) : (
                      <Tag style={{ fontSize: 11, margin: 0, color: '#8c8c8c' }}>IPs: All allowed</Tag>
                    )}
                    {webhookUrl && (
                      <Tag color="purple" style={{ fontSize: 11, margin: 0 }}>
                        <GlobalOutlined /> Webhook
                      </Tag>
                    )}
                  </div>

                  {isLegacy && keyPrefix && (
                    <div className="key-legacy-row">
                      <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                        Legacy key: {keyPrefix}...
                      </Text>
                    </div>
                  )}

                  {scopes.length > 0 && (
                    <div className="scopes-row">
                      {scopes.slice(0, 4).map((scope) => (
                        <Tag key={scope} color="blue" style={{ fontSize: 11 }}>{scope}</Tag>
                      ))}
                      {scopes.length > 4 && (
                        <Tooltip title={scopes.slice(4).join(', ')}>
                          <Tag style={{ fontSize: 11 }}>+{scopes.length - 4} more</Tag>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>

                <div className="key-card-right">
                  <div className="key-meta">
                    <span><ClockCircleOutlined /> Created: {(key.created_at || key.createdAt) ? moment(key.created_at || key.createdAt).format('MMM D, YYYY') : '—'}</span>
                    {(key.last_used_at || key.lastUsedAt) && <span>Last used: {moment(key.last_used_at || key.lastUsedAt).fromNow()}</span>}
                    {totalReq > 0 && <span><ApiOutlined /> Requests: {totalReq.toLocaleString()}</span>}
                    {expiresAt && isActive && !isExpired && (
                      <span>Expires: {moment(expiresAt).format('MMM D, YYYY')}</span>
                    )}
                    {isRevoked && (key.revoked_at || key.revokedAt) && (
                      <span style={{ color: '#ff4d4f' }}>Revoked: {moment(key.revoked_at || key.revokedAt).format('MMM D, YYYY')}</span>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })
      )}
              </>
            ),
          },
          {
            key: 'activity',
            label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><HistoryOutlined /> Recent API Activity</span>,
            children: (
              <div className="audit-logs-section">
                <div className="section-header">
                  <Title level={5} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <HistoryOutlined style={{ color: '#1890ff' }} /> Recent API Activity
                  </Title>
                  <Button size="small" icon={<ReloadOutlined />} onClick={() => fetchAuditLogs(auditPage)} loading={auditLoading}>Refresh</Button>
                </div>
                <Table
                  dataSource={auditLogs}
                  columns={auditColumns}
                  rowKey={(r) => r._id || r.id || r.created_at}
                  loading={auditLoading}
                  size="small"
                  pagination={{
                    current: auditPage, total: auditTotal, pageSize: AUDIT_PAGE_SIZE, showSizeChanger: false,
                    onChange: (page) => { setAuditPage(page); fetchAuditLogs(page); },
                  }}
                  locale={{ emptyText: <Empty description="No API activity yet" /> }}
                  scroll={{ x: 600 }}
                />
              </div>
            ),
          },
          {
            key: 'security',
            label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><WarningOutlined /> Security Events</span>,
            children: (
              <div className="security-events-section">
                <div className="section-header">
                  <Title level={5} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <WarningOutlined style={{ color: '#faad14' }} /> Security Events
                  </Title>
                  <Button size="small" icon={<ReloadOutlined />} onClick={() => fetchSecurityEvents(securityPage)} loading={securityLoading}>Refresh</Button>
                </div>
                <Table
                  dataSource={securityEvents}
                  columns={[
                    {
                      title: 'Time', dataIndex: 'created_at', key: 'time', width: 140,
                      render: (val) => (
                        <Tooltip title={moment(val).format('YYYY-MM-DD HH:mm:ss')}>
                          <span style={{ fontSize: 13 }}>{moment(val).fromNow()}</span>
                        </Tooltip>
                      ),
                    },
                    {
                      title: 'Endpoint', dataIndex: 'endpoint', key: 'endpoint',
                      render: (val, record) => (
                        <Text code style={{ fontSize: 12 }}>{record.method || 'POST'} {val}</Text>
                      ),
                    },
                    {
                      title: 'Status', dataIndex: 'status_code', key: 'status', width: 80,
                      render: (val) => (
                        <Tag color={val >= 200 && val < 300 ? 'success' : val >= 400 && val < 500 ? 'warning' : 'error'}>{val}</Tag>
                      ),
                    },
                    {
                      title: 'Key', dataIndex: 'key_name', key: 'key_name', width: 160,
                      render: (val, record) => <Text ellipsis style={{ maxWidth: 150 }}>{val || record.key_prefix || '—'}</Text>,
                    },
                    {
                      title: 'IP', dataIndex: 'ip_address', key: 'ip', width: 140,
                      render: (val) => <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{val || '—'}</Text>,
                    },
                    {
                      title: 'Response ms', dataIndex: 'response_time_ms', key: 'rt', width: 110,
                      render: (val) => <Text type="secondary" style={{ fontSize: 12 }}>{val != null ? `${val} ms` : '—'}</Text>,
                    },
                  ]}
                  rowKey={(r) => r._id || r.id || r.created_at}
                  loading={securityLoading}
                  size="small"
                  pagination={{
                    current: securityPage, total: securityTotal, pageSize: SECURITY_PAGE_SIZE, showSizeChanger: false,
                    onChange: (page) => { setSecurityPage(page); fetchSecurityEvents(page); },
                  }}
                  locale={{ emptyText: <Empty description="No security events" /> }}
                  scroll={{ x: 700 }}
                />
              </div>
            ),
          },
        ]}
      />

      {/* Modals */}
      <GenerateKeyModal
        open={generateModalOpen}
        onCancel={() => setGenerateModalOpen(false)}
        onSuccess={handleGenerateSuccess}
        wabaReady={!!onboardingStatus?.waba_connected}
      />
      <EditKeyModal
        open={editModalOpen}
        keyData={editingKey}
        onCancel={() => { setEditModalOpen(false); setEditingKey(null); }}
        onSuccess={handleEditSuccess}
        wabaReady={!!onboardingStatus?.waba_connected}
      />
      <KeyRevealModal
        open={!!revealedKeyId || !!revealedSecret}
        keyId={revealedKeyId}
        apiSecret={revealedSecret}
        apiKey={revealedApiKey}
        webhookSecret={revealedWebhookSecret}
        onClose={() => { setRevealedKeyId(null); setRevealedSecret(null); setRevealedApiKey(null); setRevealedWebhookSecret(null); }}
      />
    </div>
  );
}
